import { useMemo } from "react";
import { z } from "zod";
import { useAuth } from "../auth/authContext";
import { SupportClient } from "../integrations/supportHub/packages/client-sdk/index";
import { SupportError } from "../integrations/supportHub/packages/contracts/index";
import { SupportCenterView } from "./SupportCenterView";
import type { Locale } from "../i18n";

const sessionResponseSchema = z
  .object({
    session: z
      .object({
        accessToken: z.string().min(20),
        expiresAt: z.iso.datetime({ offset: true }),
        projectId: z.uuid(),
        actorId: z.uuid().optional(),
      })
      .strict(),
    projectKey: z.string().min(1),
  })
  .strict();
export function SupportCenter({
  locale,
}: {
  readonly locale: Locale;
}): React.JSX.Element {
  const { session, client: authClient } = useAuth();
  const userId = session?.user.id;
  const client = useMemo(() => {
    if (userId === undefined || authClient === null) return null;
    // The public key is returned by our server, never a client-chosen project identity.
    return new SupportClient({
      apiBase: "/api/support/v1",
      projectKey: "",
      getSession: async () => {
        const current = (await authClient.auth.getSession()).data.session;
        if (current?.user.id !== userId)
          throw new SupportError(401, "session_changed");
        const response = await fetch("/api/support/session", {
          method: "POST",
          headers: {
            authorization: `Bearer ${current.access_token}`,
            "content-type": "application/json",
          },
          body: "{}",
          cache: "no-store",
        });
        if (!response.ok)
          throw new SupportError(
            response.status,
            response.status === 503 ? "support_not_configured" : "unauthorized",
          );
        const value = sessionResponseSchema.parse(await response.json());
        return value.session;
      },
    });
  }, [userId, authClient]);
  return (
    <SupportCenterView
      client={client}
      identity={userId ?? "guest"}
      locale={locale === "th" ? "th" : locale === "ko" ? "ko" : "en"}
      loginUrl={`/login?lang=${locale}&next=/support`}
    />
  );
}
